/* ==========================================================================
   Vildblad 2.4 — Välkomstgåva (skraplott)
   Öppnas med theme.js lådsystem (window.Vildblad.Drawers). Sparar i webbläsaren om kunden
   har stängt rutan eller anmält sig, så att den inte visas automatiskt igen.
   ========================================================================== */
(() => {
  'use strict';

  const root = document.querySelector('[data-welcome-gift]');
  if (!root) return;
  const $ = (sel, ctx = root) => ctx.querySelector(sel);
  const sheet = $('#WelcomeGift');
  const tab = $('[data-welcome-tab]');
  const title = $('#WelcomeGiftTitle');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const design = root.dataset.design === 'true';
  const KEY = 'vb-welcome';
  const DAY = 864e5;

  /* ---------- Minne i webbläsaren (kan saknas, t.ex. i privat läge) ---------- */
  const store = {
    get() { try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (_) { return null; } },
    set(v) { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (_) { /* ignorera */ } }
  };

  const drawers = () => (window.Vildblad && window.Vildblad.Drawers) || null;
  const open = () => { const d = drawers(); if (d) d.open('WelcomeGift', tab); };

  /* ---------- Steg ---------- */
  const setStep = (step, focus = true) => {
    sheet.dataset.step = step;
    root.querySelectorAll('[data-step-panel]').forEach((p) => { p.hidden = p.dataset.stepPanel !== step; });
    const t = title && title.dataset[`title${step.charAt(0).toUpperCase()}${step.slice(1)}`];
    if (t) title.textContent = t;
    if (!focus) return;
    if (step === 'form') setTimeout(() => $('#WelcomeGiftEmail')?.focus({ preventScroll: true }), 60);
    if (step === 'done') setTimeout(() => $('[data-welcome-copy]')?.focus({ preventScroll: true }), 60);
  };

  /* ---------- Skraplotten ---------- */
  const scratch = $('[data-scratch]');
  const canvas = $('[data-scratch-canvas]');
  let ctx = null;
  let drawn = false;
  let revealed = false;
  let moves = 0;
  let last = null;

  const paintCover = () => {
    if (!canvas || !scratch) return;
    const rect = scratch.getBoundingClientRect();
    if (!rect.width) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const w = rect.width;
    const h = rect.height;
    const css = getComputedStyle(document.documentElement);
    const primary = css.getPropertyValue('--primary').trim() || '#35432F';
    const sage = css.getPropertyValue('--sage').trim() || '#A9B79A';
    // Bakgrund: mossgrön med mjuk ljusning
    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, primary);
    g.addColorStop(1, sage);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    // Små blad som mönster
    ctx.strokeStyle = 'rgba(251, 248, 242, .16)';
    ctx.lineWidth = 1.4;
    ctx.lineCap = 'round';
    for (let y = 14; y < h + 20; y += 34) {
      for (let x = (y / 34) % 2 ? 10 : 30; x < w + 20; x += 40) {
        ctx.beginPath();
        ctx.moveTo(x, y + 10);
        ctx.quadraticCurveTo(x + 2, y - 2, x + 12, y - 6);
        ctx.quadraticCurveTo(x + 12, y + 6, x, y + 10);
        ctx.stroke();
      }
    }
    // Text
    ctx.fillStyle = 'rgba(251, 248, 242, .95)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const font = css.getPropertyValue('--font-body').trim() || 'system-ui, sans-serif';
    ctx.font = `600 ${Math.round(Math.min(22, w / 14))}px ${font}`;
    ctx.fillText(canvas.dataset.label || 'Skrapa här', w / 2, h / 2);
    ctx.globalCompositeOperation = 'destination-out';
    drawn = true;
  };

  const clearedRatio = () => {
    if (!ctx) return 0;
    const { width, height } = canvas;
    const data = ctx.getImageData(0, 0, width, height).data;
    let clear = 0;
    let total = 0;
    const step = 4 * 24; // var 24:e pixel räcker
    for (let i = 3; i < data.length; i += step) { total += 1; if (data[i] < 40) clear += 1; }
    return total ? clear / total : 0;
  };

  const reveal = () => {
    if (revealed) return;
    revealed = true;
    scratch?.classList.add('is-revealed');
    setTimeout(() => setStep('form'), reduceMotion ? 0 : 900);
  };

  const pointFrom = (e) => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const scratchAt = (p) => {
    if (!ctx) return;
    const r = Math.max(18, canvas.getBoundingClientRect().width / 16);
    ctx.lineWidth = r * 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    if (last) { ctx.moveTo(last.x, last.y); ctx.lineTo(p.x, p.y); ctx.stroke(); }
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fill();
    last = p;
    moves += 1;
    if (moves % 6 === 0 && clearedRatio() > 0.45) reveal();
  };

  if (canvas) {
    let down = false;
    canvas.addEventListener('pointerdown', (e) => {
      if (revealed) return;
      if (!drawn) paintCover();
      down = true;
      last = null;
      try { canvas.setPointerCapture(e.pointerId); } catch (_) { /* ignorera */ }
      scratchAt(pointFrom(e));
    });
    canvas.addEventListener('pointermove', (e) => {
      if (!down || revealed) return;
      e.preventDefault();
      scratchAt(pointFrom(e));
    });
    const up = () => {
      if (!down) return;
      down = false;
      last = null;
      if (!revealed && clearedRatio() > 0.45) reveal();
    };
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
  }
  $('[data-scratch-reveal]')?.addEventListener('click', reveal);

  // Rita täckytan när rutan öppnas (måtten finns först då)
  sheet.addEventListener('drawer:open', () => {
    if (sheet.dataset.step === 'scratch' && !revealed) requestAnimationFrame(() => requestAnimationFrame(paintCover));
  });
  window.addEventListener('resize', () => { if (!revealed && sheet.classList.contains('is-open')) paintCover(); });

  /* ---------- Formuläret ---------- */
  const form = $('[data-welcome-form]');
  const phone = $('[data-welcome-phone]');
  const message = $('[data-welcome-message]');

  // Svenska mobilnummer (07…) eller internationellt nummer med +
  const normalizePhone = (raw) => {
    const v = (raw || '').replace(/[\s\-().]/g, '');
    if (/^07\d{8}$/.test(v)) return `+46${v.slice(1)}`;
    if (/^0046 ?7\d{8}$/.test(v)) return `+46${v.slice(4)}`;
    if (/^\+467\d{8}$/.test(v)) return v;
    if (/^\+[1-9]\d{7,14}$/.test(v) && !v.startsWith('+46')) return v;
    return null;
  };
  phone?.addEventListener('input', () => phone.setCustomValidity(''));

  const done = () => {
    store.set({ state: 'done', t: Date.now() });
    if (tab) tab.hidden = true;
    setStep('done');
  };

  const showError = (text) => {
    if (!message) return;
    message.textContent = text;
    message.hidden = false;
  };

  const post = async (data) => {
    const res = await fetch(form.getAttribute('action') || '/contact', { method: 'POST', body: data, headers: { Accept: 'text/html' } });
    if (/\/challenge/.test(res.url)) return { challenge: true };
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const fresh = doc.getElementById('WelcomeGiftForm');
    if (!fresh) return { error: 'no-form' };
    if (fresh.querySelector('[data-welcome-success]')) return { ok: true };
    const err = fresh.querySelector('[data-welcome-error]');
    return { error: err ? err.textContent.trim() : 'unknown' };
  };

  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (message) message.hidden = true;
    const number = normalizePhone(phone.value);
    if (!number) {
      phone.setCustomValidity(phone.dataset.invalid || 'Kontrollera mobilnumret.');
    } else {
      phone.setCustomValidity('');
    }
    if (!form.checkValidity()) { form.reportValidity(); return; }

    const today = new Date().toISOString().slice(0, 10);
    const note = `Mobil: ${number}. Samtycke till nyhetsbrev via e-post och sms (välkomstgåvan ${today}).`;
    const noteField = $('[data-welcome-note]', form);
    if (noteField) noteField.value = note;

    const btn = $('button[type="submit"]', form);
    btn?.classList.add('is-loading');
    btn?.setAttribute('aria-busy', 'true');
    try {
      let result = await post(new FormData(form));
      // Finns kunden redan går anteckningen inte att ändra – spara numret som tagg i stället
      if (result.error && !result.ok && !result.challenge) {
        const retry = new FormData(form);
        retry.delete('contact[note]');
        const tags = `${retry.get('contact[tags]')}, mobil-${number.replace('+', '')}`;
        retry.set('contact[tags]', tags);
        result = await post(retry);
      }
      if (result.challenge) {
        // Shopify vill visa en robotkontroll: skicka formuläret på vanligt sätt
        store.set({ state: 'pending', t: Date.now() });
        HTMLFormElement.prototype.submit.call(form);
        return;
      }
      if (result.ok) { done(); return; }
      showError(result.error && result.error !== 'unknown' && result.error !== 'no-form' ? result.error : (message?.dataset.fallback || 'Något gick fel. Försök igen om en stund.'));
    } catch (_) {
      showError(message?.dataset.fallback || 'Något gick fel. Försök igen om en stund.');
    } finally {
      btn?.classList.remove('is-loading');
      btn?.removeAttribute('aria-busy');
    }
  });

  /* ---------- Kopiera koden ---------- */
  const copyBtn = $('[data-welcome-copy]');
  copyBtn?.addEventListener('click', async () => {
    const code = ($('[data-welcome-code]')?.textContent || '').trim();
    let ok = false;
    try { await navigator.clipboard.writeText(code); ok = true; } catch (_) {
      const range = document.createRange();
      const el = $('[data-welcome-code]');
      if (el) { range.selectNodeContents(el); const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range); ok = document.execCommand && document.execCommand('copy'); }
    }
    if (ok) {
      copyBtn.classList.add('is-copied');
      const label = copyBtn.querySelector('span');
      if (label) { label.dataset.text = label.dataset.text || label.textContent; label.textContent = copyBtn.dataset.copied || 'Kopierad'; }
      setTimeout(() => { copyBtn.classList.remove('is-copied'); if (label) label.textContent = label.dataset.text; }, 2400);
    }
  });

  /* ---------- När rutan stängs ---------- */
  sheet.addEventListener('drawer:close', () => {
    const s = store.get();
    if (!s || s.state !== 'done') store.set({ state: 'closed', t: Date.now() });
    if (tab && (store.get() || {}).state !== 'done') {
      tab.hidden = false;
      if (!reduceMotion) { tab.classList.add('is-entering'); setTimeout(() => tab.classList.remove('is-entering'), 600); }
    }
  });

  /* ---------- När rutan visas ---------- */
  const state = store.get();
  const known = root.dataset.known === 'true';

  // Efter en vanlig sidomladdning (t.ex. efter robotkontroll) och lyckad anmälan: visa koden
  if (form && form.querySelector('[data-welcome-success]')) {
    store.set({ state: 'done', t: Date.now() });
    revealed = true;
    scratch?.classList.add('is-revealed');
    setStep('done', false);
    const go = () => open();
    if (document.readyState === 'complete') go(); else window.addEventListener('load', go);
    return;
  }
  if (form && form.querySelector('[data-welcome-error]')) {
    revealed = true;
    scratch?.classList.add('is-revealed');
    setStep('form', false);
    const go = () => open();
    if (document.readyState === 'complete') go(); else window.addEventListener('load', go);
  }

  if (design) {
    // Temaredigeraren: visa knappen och öppna rutan när sektionen väljs
    if (tab) tab.hidden = false;
    document.addEventListener('shopify:section:select', (e) => { if (e.detail && e.detail.sectionId === root.dataset.sectionId) open(); });
    document.addEventListener('shopify:section:deselect', (e) => { if (e.detail && e.detail.sectionId === root.dataset.sectionId) drawers()?.close(sheet, false); });
    return;
  }

  if ((state && state.state === 'done') || known) return; // anmäld: varken knapp eller ruta

  const days = parseInt(root.dataset.days, 10) || 14;
  const recentlyClosed = state && state.state === 'closed' && (Date.now() - state.t) < days * DAY;

  // Knappen nere till vänster visas efter några sekunder
  setTimeout(() => {
    if (!tab) return;
    tab.hidden = false;
    if (!reduceMotion) { tab.classList.add('is-entering'); setTimeout(() => tab.classList.remove('is-entering'), 600); }
  }, recentlyClosed ? 1500 : 4000);

  if (recentlyClosed || root.dataset.auto !== 'true') return;
  // På produktsidor i mobilen stör en ruta som öppnar sig själv köpet – där räcker fliken
  if (document.body.classList.contains('template-product') && window.matchMedia('(max-width: 749px)').matches) return;

  // Öppna automatiskt en gång: efter en stund eller när kunden scrollat en bit
  let fired = false;
  const delay = (parseInt(root.dataset.delay, 10) || 15) * 1000;
  const scrollShare = (parseInt(root.dataset.scroll, 10) || 50) / 100;
  const tryOpen = () => {
    if (fired) return;
    const d = drawers();
    if (!d) return;
    if (d.current) { setTimeout(tryOpen, 4000); return; } // vänta tills en annan låda (t.ex. varukorgen) är stängd
    if (document.documentElement.classList.contains('cc-open')) { setTimeout(tryOpen, 4000); return; } // vänta tills kakrutan är besvarad
    fired = true;
    window.removeEventListener('scroll', onScroll);
    open();
  };
  const onScroll = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (max > 0 && window.scrollY / max >= scrollShare) tryOpen();
  };
  setTimeout(tryOpen, delay);
  window.addEventListener('scroll', onScroll, { passive: true });
})();
